<script lang="ts">
  import { untrack } from "svelte";
  /* Shared price matrix used by the Distributors, My Shops and Partner Shops
     pricing tabs. Renders product reference columns (EAN / Qty per box /
     Weight), price formation (purchase × coefficient = base sale price), and
     one price column per target (distributor / shop). Cells whose price
     diverges from the base are highlighted; a price the client can't see
     (access off) renders dimmed.

     PRICES AND ACCESS (owner, 2026-09-16): a client's price means nothing
     without the client's access to the product, so the «Доступ позиций» page
     is gone and its controls are back here — a small checkbox left of each
     client price (or the gold star when the client distributes it
     exclusively), the whole filtered view from the checkbox under the
     client's name, and the «Права эксклюзивности» star mode on the second
     filter row. They render only where the caller wires the handlers.
     (2026-08-13 had split them out; the 2026-08-15 rule stands: access is
     the master switch, a price never opens it.)

     A card switched to «not available to order» on the Products list is
     greyed down its whole row: still priced and opened as usual, but no
     client can put it on an order until it is back.

     The reference columns are the user's to arrange: which of them are on, and
     in what order, is chosen in the same "Screen layout settings" panel the
     Products list uses, and saved per browser. The target columns are the data
     the screen is for, so they are always shown, always last.

     There is no band above the target columns naming what they are. The tab
     you are on already says whether these are distributors, your own shops or
     partner shops; repeating it over the columns was a header that told you
     what you had just clicked. */
  import { _ } from "svelte-i18n";
  import InfoTooltip from "$components/InfoTooltip.svelte";
  import SupplierGapBadge from "$components/SupplierGapBadge.svelte";
  import ColumnSettings, { type ColumnDef } from "$components/ColumnSettings.svelte";
  import SearchSelect from "$components/SearchSelect.svelte";
  import { formatBarcode, priceDecimals } from "$lib/format";
  import { makesItsGoods } from "$lib/companyRoles";
  import { auth } from "$lib/auth";
  import { inCategory, knownCategories, loadCatalogFilters, saveCatalogFilters } from "$lib/catalogFilters";
  import CatalogFilterBar from "$components/CatalogFilterBar.svelte";
  import { columnWidths } from "$lib/columnWidths.svelte";
  import { scaleToDisplay, weightScaleForUnit, type Category } from "$lib/catalog";

  export type Target = { id: number; name: string; country?: string };
  /* `target_id` is null on the rows the API returns when the tenant has no
     distributors / shops linked yet — the product is still listed so its base
     price can be set, it just has no per-target cell. */
  export type FlatRow = {
    product_id: number;
    product_name: string;
    product_code: string;
    ean: string;
    net_weight_kg: number | null;
    /** The unit the card is sold by — weight is displayed in it. */
    unit?: string;
    /** Cover photo for the optional thumbnail column. */
    image_url?: string | null;
    thumb_url?: string | null;
    /** Free-text supplier label off the product card — the filter keys on it. */
    supplier?: string;
    /** «Недоступна у поставщика» on our copy of a supplier card. */
    supplier_gap_reason?: string;
    base_price: number | null;
    /** Price formation: purchase price + markup %, per product. */
    cost_price?: number | null;
    markup_pct?: number | null;
    base_to_all: boolean;
    category_id: number | null;
    target_id: number | null;
    price: number | null;
    is_accessible: boolean;
    /** Exclusive-distribution star for this (product, client) pair. */
    is_exclusive?: boolean;
    /** «Доступна к заказу» off on the card: the row is greyed. */
    is_orderable?: boolean;
  };

  let {
    tenantSlug = "",
    loading = false,
    flatRows = [],
    targets = [],
    categories = [],
    emptyText = "",
    infoNote = "",
    onHideNote = undefined,
    onSaveBase,
    onSavePrice,
    onToggleAccessible,
    onBulkAccessible = undefined,
    onBaseToTarget = undefined,
    onToggleExclusive = undefined,
    onBulkExclusive = undefined,
    onFormation = undefined,
    accessEditable = true,
  }: {
    tenantSlug?: string;
    loading?: boolean;
    flatRows?: FlatRow[];
    targets?: Target[];
    categories?: Category[];
    emptyText?: string;
    /** One-line banner over the matrix — what it takes for a client to
     *  actually see these rows (access open + price set). */
    infoNote?: string;
    /** Given, the note gets a «Скрыть» at its right end. */
    onHideNote?: () => void;
    onSaveBase: (productId: number, value: string) => void;
    onSavePrice: (productId: number, targetId: number, value: string) => void;
    /** Access to one (product, client) pair — the checkbox left of the price. */
    onToggleAccessible?: (productId: number, targetId: number, value: boolean) => void;
    onBulkAccessible?: (targetId: number, productIds: number[], value: boolean) => void;
    /** Header checkbox in a target column: copy the Base price onto that
     *  client for every product in the filtered view. */
    onBaseToTarget?: (targetId: number, productIds: number[]) => void;
    onToggleExclusive?: (productId: number, targetId: number, value: boolean) => void;
    onBulkExclusive?: (targetId: number, productIds: number[], value: boolean) => void;
    /** Price formation write: purchase price + markup %, and the recomputed
     *  base sale price when it changed. Enables the two formation columns. */
    onFormation?: (
      productId: number,
      data: { cost_price: number; markup_pct: number; base_price: number | null },
    ) => void;
    /** False for a reader who may look but not open or close access. */
    accessEditable?: boolean;
  } = $props();

  /** Every price on this screen is written to the same number of decimals — a
   *  price list that reads 2.1 next to 1.95 is one you have to squint at to
   *  compare. How many is the company's own choice (Prices → Settings): two
   *  for money, three where the margin lives in the third digit. */
  const decimals = $derived(priceDecimals(tenantSlug));
  function money(v: number | null | undefined): string {
    return v == null ? "" : Number(v).toFixed(decimals);
  }

  // ── Columns ───────────────────────────────────────────────────────────
  /* Weight carries its unit on the value, not in the heading — and in the unit
     the product is sold by (a 50 g snack reads "50 g", not "0.05 kg"), exactly
     as the Products list shows it. */

  /* There is no «Базовую всем» column any more (2026-08-16): pushing the base
     price onto everyone at once is the same act as pushing it onto one client,
     and that already lives where it belongs — the checkbox under each client's
     name in the frozen header row. One column per client, one checkbox each,
     and no extra column in the middle of the reference data. */
  type ColKey =
    | "image" | "code" | "ean" | "category" | "subcategory" | "weight"
    | "costPrice" | "markup";
  const COL_KEYS: ColKey[] = [
    "image", "code", "ean", "category", "subcategory", "weight",
    "costPrice", "markup",
  ];
  const DEFAULT_VISIBLE: ColKey[] = ["image", "code", "ean", "weight", "costPrice", "markup"];

  const COLS_STORE_KEY = "prices.columns.selected.v1";
  const COL_ORDER_STORE_KEY = "prices.columns.order.v1";

  /* The two formation columns only exist where formation writes are wired
     (the client-access tab) AND the company opted in (Prices → Settings:
     «Себестоимость, коэффициент и базовая цена продажи»). A company that
     doesn't track per-client profit sees only the base sale price. */
  const formationOn = $derived(
    $auth.memberships.find((m) => m.tenant.slug === tenantSlug)?.tenant
      .show_price_formation ?? false,
  );
  const COLUMNS = $derived<ColumnDef[]>(
    [
      { key: "image", label: $_("pl.col.image") },
      { key: "product", label: $_("prices.col.product"), locked: true },
      { key: "code", label: $_("prices.col.code") },
      { key: "ean", label: $_("prices.col.ean") },
      { key: "category", label: $_("pf.f.category") },
      { key: "subcategory", label: $_("pf.f.subcategory") },
      { key: "weight", label: $_("pl.col.weight") },
      { key: "costPrice", label: makesItsGoods($auth, tenantSlug) ? $_("prices.col.productionCost") : $_("prices.col.costPrice") },
      { key: "markup", label: $_("prices.col.markup") },
      { key: "basePrice", label: $_("prices.col.basePrice"), locked: true },
    ].filter((c) => (onFormation && formationOn) || (c.key !== "costPrice" && c.key !== "markup")),
  );

  function loadVisible(): Record<string, boolean> {
    /* Keys absent from the stored map (columns added after the user last
       saved) fall back to their shipped default, not to hidden. */
    const defaults = {
      ...Object.fromEntries(COL_KEYS.map((k) => [k, false])),
      ...Object.fromEntries(DEFAULT_VISIBLE.map((k) => [k, true])),
    };
    try {
      const stored = localStorage.getItem(COLS_STORE_KEY);
      if (stored) return { ...defaults, ...(JSON.parse(stored) as Record<string, boolean>) };
    } catch {
      /* ignore malformed */
    }
    return defaults;
  }

  /* Order covers the locked columns too — "Product" and "Base price" are on
     screen, so they are draggable like everything else. */
  const ORDERABLE = [
    "image", "product", "code", "ean", "category", "subcategory", "weight",
    "costPrice", "markup", "basePrice",
  ];
  function loadOrder(): string[] {
    try {
      const stored = localStorage.getItem(COL_ORDER_STORE_KEY);
      if (stored) {
        const saved = (JSON.parse(stored) as string[]).filter((k) => ORDERABLE.includes(k));
        /* Columns added after the order was saved slot in at their shipped
           position relative to what the user kept (the thumbnail at the left
           edge, the formation pair right before Base price) — appending them
           at the end would strand them behind Base-to-All. */
        const merged = [...saved];
        for (const k of ORDERABLE) {
          if (merged.includes(k)) continue;
          const following = ORDERABLE.slice(ORDERABLE.indexOf(k) + 1);
          const at = merged.findIndex((m) => following.includes(m));
          if (at === -1) merged.push(k);
          else merged.splice(at, 0, k);
        }
        return merged;
      }
    } catch {
      /* ignore malformed */
    }
    return [...ORDERABLE];
  }

  let columnsOpen = $state(false);
  let colVisible = $state<Record<string, boolean>>(loadVisible());
  let colOrder = $state<string[]>(loadOrder());

  $effect(() => {
    localStorage.setItem(COLS_STORE_KEY, JSON.stringify(colVisible));
  });
  $effect(() => {
    localStorage.setItem(COL_ORDER_STORE_KEY, JSON.stringify(colOrder));
  });

  const colRank = (key: string) => {
    const i = colOrder.indexOf(key);
    return i === -1 ? colOrder.length + ORDERABLE.indexOf(key) : i;
  };
  /** The reference columns on screen, left to right. */
  // Column widths the user dragged in the header, remembered in this browser.
  const colWidths = columnWidths("prices");
  const shownCols = $derived(
    COLUMNS.filter((c) => c.locked || colVisible[c.key]).sort(
      (a, b) => colRank(a.key) - colRank(b.key),
    ),
  );
  // ── Filters ───────────────────────────────────────────────────────────
  // This is a client-facing screen (2026-08-13 feedback): the filters are the
  // catalogue's plus WHO — clients and their countries, on the second row.
  //
  // The catalogue half is the row every catalogue screen shares (2026-09-13,
  // $components/CatalogFilterBar): search, category, subcategory, supplier,
  // remembered across Products, «Доступ позиций», the other tabs and Склад.
  // The supplier picker used to be a distributor's only (2026-08-30); the
  // row is now the same for every company, and where no card names a
  // supplier the picker is simply disabled.
  const sharedFilters = untrack(() => loadCatalogFilters($auth.user?.id, tenantSlug));
  let selectedSupplier = $state(sharedFilters.supplier);
  const supplierOptions = $derived(
    [...new Set(flatRows.map((r) => (r.supplier ?? "").trim()).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    ),
  );
  /* A supplier that disappears from the catalogue (renamed on the cards, or
     the last card carrying it deleted) must not leave the matrix filtered by
     a name nothing matches — that reads as "my products are gone". */
  $effect(() => {
    if (!loading && selectedSupplier && !supplierOptions.includes(selectedSupplier)) selectedSupplier = "";
  });
  let selectedCategory = $state(sharedFilters.category);
  let selectedSubcategory = $state(sharedFilters.subcategory);
  let searchQ = $state(sharedFilters.q);
  $effect(() => {
    saveCatalogFilters($auth.user?.id, tenantSlug, {
      q: searchQ,
      supplier: selectedSupplier,
      category: selectedCategory,
      subcategory: selectedSubcategory,
    });
  });
  // Once the tree is in, a remembered category it no longer has is dropped.
  $effect(() => {
    if (categories.length === 0) return;
    const known = knownCategories(
      categories,
      untrack(() => selectedCategory),
      untrack(() => selectedSubcategory),
    );
    selectedCategory = known.category;
    selectedSubcategory = known.subcategory;
  });
  function matchesCategory(catId: number | null): boolean {
    return inCategory(categories, catId, selectedCategory, selectedSubcategory);
  }

  /* ── Client + country filters ──────────────────────────────────────────
     Fifty clients don't fit side by side as columns; the pickers narrow the
     matrix to the client (or the country of clients) being worked on. The
     country list holds only countries of confirmed clients. */
  let selectedClient = $state("");
  let selectedCountry = $state("");
  const clientOptions = $derived([
    { value: "", label: $_("prices.filter.allClients") },
    ...targets.map((t) => ({ value: String(t.id), label: t.name })),
  ]);
  const countryOptions = $derived(
    [...new Set(targets.map((t) => (t.country ?? "").toUpperCase()).filter(Boolean))].sort(),
  );
  const visibleTargets = $derived(
    targets.filter(
      (t) =>
        (!selectedClient || String(t.id) === selectedClient) &&
        (!selectedCountry || (t.country ?? "").toUpperCase() === selectedCountry),
    ),
  );

  /* A reference column's header sorts the rows, as on Склад (2026-09-19). */
  let sortKey = $state("");
  let sortDir = $state<"asc" | "desc">("asc");

  type Group = {
    product_id: number;
    product_name: string;
    product_code: string;
    ean: string;
    net_weight_kg: number | null;
    unit: string;
    image_url: string | null;
    thumb_url: string | null;
    supplier: string;
    supplier_gap_reason: string;
    base_price: number | null;
    cost_price: number | null;
    markup_pct: number | null;
    category_id: number | null;
    is_orderable: boolean;
    cells: Map<number, { price: number | null; is_accessible: boolean; is_exclusive: boolean }>;
  };

  const grouped = $derived((() => {
    const m = new Map<number, Group>();
    for (const r of flatRows) {
      let g = m.get(r.product_id);
      if (!g) {
        g = {
          product_id: r.product_id,
          product_name: r.product_name,
          product_code: r.product_code,
          ean: r.ean,
          net_weight_kg: r.net_weight_kg,
          unit: r.unit || "g",
          image_url: r.image_url ?? null,
          thumb_url: r.thumb_url ?? null,
          supplier: (r.supplier ?? "").trim(),
          supplier_gap_reason: r.supplier_gap_reason ?? "",
          base_price: r.base_price,
          cost_price: r.cost_price ?? null,
          markup_pct: r.markup_pct ?? null,
          category_id: r.category_id,
          is_orderable: r.is_orderable ?? true,
          cells: new Map(),
        };
        m.set(r.product_id, g);
      }
      if (r.target_id != null) {
        g.cells.set(r.target_id, {
          price: r.price,
          is_accessible: r.is_accessible,
          is_exclusive: r.is_exclusive ?? false,
        });
      }
    }
    const q = searchQ.trim().toLowerCase();
    const out = [...m.values()].filter(
      (g) =>
        matchesCategory(g.category_id) &&
        (!selectedSupplier || g.supplier === selectedSupplier) &&
        (!q ||
          g.product_name.toLowerCase().includes(q) ||
          g.product_code.toLowerCase().includes(q) ||
          (g.ean || "").toLowerCase().includes(q)),
    );
    const key = sortKey;
    if (!key) return out;
    const sign = sortDir === "asc" ? 1 : -1;
    return out.sort((a, b) => {
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return (typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb))) * sign;
    });
  })());

  function sortBy(key: string) {
    if (sortKey === key) sortDir = sortDir === "asc" ? "desc" : "asc";
    else { sortKey = key; sortDir = "asc"; }
  }
  const sortIcon = (key: string) =>
    sortKey !== key ? "unfold_more" : sortDir === "asc" ? "arrow_upward" : "arrow_downward";
  function sortValue(g: Group, key: string): string | number | null {
    const num = (v: number | null) => (v == null ? null : Number(v));
    switch (key) {
      case "weight": return num(g.net_weight_kg);
      case "costPrice": return num(g.cost_price);
      case "markup": return num(g.markup_pct);
      case "basePrice": return num(g.base_price);
      default: {
        const t = cellText(g, key);
        return !t || t === "-" ? null : t.toLocaleLowerCase();
      }
    }
  }

  /** One reference cell's text, by column. Money and weight carry their
   *  formatting here so the header can stay a plain noun. */
  function cellText(g: Group, key: string): string {
    switch (key) {
      case "product": return g.product_name;
      case "code": return g.product_code;
      case "ean": return formatBarcode(g.ean) || "-";
      case "category": return categoryNames(g.category_id).group;
      case "subcategory": return categoryNames(g.category_id).sub;
      case "weight": {
        if (g.net_weight_kg == null) return "-";
        const s = weightScaleForUnit(g.unit);
        return `${scaleToDisplay(g.net_weight_kg, s)} ${s.unit}`;
      }
      default: return "";
    }
  }

  /* A product's category_id stores the leaf: the subcategory when it has a
     parent, otherwise the top-level category itself. */
  const catById = $derived(new Map(categories.map((c) => [c.id, c])));
  function categoryNames(catId: number | null): { group: string; sub: string } {
    const c = catId != null ? catById.get(catId) : undefined;
    if (!c) return { group: "-", sub: "-" };
    if (c.parent_id != null) return { group: catById.get(c.parent_id)?.name ?? "-", sub: c.name };
    return { group: c.name, sub: "-" };
  }

  /* ── Access (moved in from «Доступ позиций», 2026-09-16) ───────────────
     One control per (product, client) cell, left of the price: the checkbox
     opens the product to the client; a gold star is access AND exclusive
     distribution and replaces the checkbox. With «Права эксклюзивности» on,
     clicks (in the cells and under the client's name) grant or withdraw the
     star instead of plain access. */
  const accessOn = $derived(!!onToggleAccessible);
  let exclusiveMode = $state(false);
  function targetAllAccessible(tid: number): boolean {
    return grouped.length > 0 && grouped.every((g) => g.cells.get(tid)?.is_accessible ?? false);
  }
  function targetAllExclusive(tid: number): boolean {
    return grouped.length > 0 && grouped.every((g) => g.cells.get(tid)?.is_exclusive ?? false);
  }
  function toggleCellAccess(g: Group, tid: number) {
    if (!accessEditable) return;
    const cell = g.cells.get(tid);
    if (exclusiveMode) onToggleExclusive?.(g.product_id, tid, !(cell?.is_exclusive ?? false));
    else onToggleAccessible?.(g.product_id, tid, !(cell?.is_accessible ?? false));
  }
  function toggleTargetAccess(tid: number) {
    if (!accessEditable || grouped.length === 0) return;
    const ids = grouped.map((g) => g.product_id);
    if (exclusiveMode) onBulkExclusive?.(tid, ids, !targetAllExclusive(tid));
    else onBulkAccessible?.(tid, ids, !targetAllAccessible(tid));
  }

  /** A row with a base price worth copying onto a client. */
  const canPush = (g: Group) => g.base_price != null && g.base_price > 0;

  /* A hand-set client price that departs from the base is tinted yellow —
     however it got there (after a Base-to-All push or typed directly). The
     tint is what says "this client is priced specially". */
  function isOverridden(g: Group, price: number | null): boolean {
    return price != null && g.base_price != null && price !== g.base_price;
  }

  // Hover tooltips (bulk-base checkbox). A single fixed-positioned element
  // avoids being clipped by the matrix's overflow:auto scroll area.
  let tip = $state<{ text: string; x: number; y: number } | null>(null);
  function showTip(e: MouseEvent, text: string) {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    tip = { text, x: r.left + r.width / 2, y: r.top };
  }
  function hideTip() {
    tip = null;
  }

  /* Thumbnail hover preview — same behaviour as the Products list: the 40px
     thumbnail says "has a photo", hovering shows it at a judgeable size. */
  const PREVIEW_MAX = 320;
  let imgPreview = $state<{ url: string; x: number; y: number } | null>(null);
  function showImgPreview(e: MouseEvent, url: string) {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x =
      r.right + 12 + PREVIEW_MAX <= window.innerWidth
        ? r.right + 12
        : Math.max(8, r.left - 12 - PREVIEW_MAX);
    const y = Math.min(
      Math.max(8, r.top + r.height / 2 - PREVIEW_MAX / 2),
      Math.max(8, window.innerHeight - PREVIEW_MAX - 8),
    );
    imgPreview = { url, x, y };
  }
  function hideImgPreview() {
    imgPreview = null;
  }

  /* Enter walks the editable cells: next field in the same row, then on to
     the next row. Moving focus blurs the current input, and blur is what
     saves — so Enter never double-writes. */
  function focusNextInput(e: KeyboardEvent) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const current = e.target as HTMLInputElement;
    const table = current.closest("table");
    if (!table) { current.blur(); return; }
    const inputs = [...table.querySelectorAll<HTMLInputElement>("input.price-input")];
    const next = inputs[inputs.indexOf(current) + 1];
    if (next) { next.focus(); next.select(); }
    else current.blur();
  }

  /* ── Per-client header checkbox: Base price → this client ─────────────
     Acts on the filtered view (a category, a subcategory, or the whole
     assortment when no filter is set) — but only on the rows OPEN to this
     client. Access is the master switch (2026-08-15): pricing never opens
     access, so closed rows (the dimmed ones) are left untouched; they are
     opened with the checkbox left of the price. */
  /** Rows the push applies to for `tid`: base price set AND access open. */
  function targetPushableRows(tid: number): Group[] {
    return grouped.filter((g) => canPush(g) && (g.cells.get(tid)?.is_accessible ?? true));
  }
  /** Every pushable row already carries the base price for `tid`. */
  function targetAllAtBase(tid: number): boolean {
    const rows = targetPushableRows(tid);
    return rows.length > 0 && rows.every((g) => g.cells.get(tid)?.price === g.base_price);
  }
  function applyBaseToTarget(t: Target) {
    if (!onBaseToTarget) return;
    const ids = targetPushableRows(t.id).map((g) => g.product_id);
    if (ids.length) onBaseToTarget(t.id, ids);
  }

  /* ── Price formation: закупка × коэффициент = базовая цена продажи ────
     Three linked numbers; editing any one recomputes the others. Cost and
     coefficient anchor the base; a hand-set base recomputes the coefficient
     backwards. All writes go through onFormation in one shot. */
  function coefOf(g: Group): number | null {
    /* Read off the row, not remembered: a purchase price that moves changes
       the coefficient, never the sale price (owner, 2026-09-19). */
    if (g.cost_price != null && g.cost_price > 0 && g.base_price != null && g.base_price > 0) {
      return g.base_price / g.cost_price;
    }
    return g.markup_pct == null ? null : 1 + g.markup_pct / 100;
  }
  function coefText(g: Group): string {
    const k = coefOf(g);
    return k == null ? "" : k.toFixed(2);
  }
  function saveFormation(g: Group, field: "cost" | "coef" | "base", raw: string) {
    if (!onFormation) return;
    const v = parseFloat(raw);
    if (isNaN(v)) return;
    let cost = g.cost_price;
    let pct = g.markup_pct;
    let base = g.base_price;
    const round = (x: number) => Math.round(x * 10 ** decimals) / 10 ** decimals;
    if (field === "cost") {
      if (v < 0) return;
      cost = v;
      // The sale price stays where it was put; the coefficient drifts.
      if (base != null && base > 0 && cost > 0) pct = (base / cost - 1) * 100;
    } else if (field === "coef") {
      if (v <= 0) return;
      pct = (v - 1) * 100;
      if (cost != null && cost > 0) base = round(cost * v);
    } else {
      if (v < 0) return;
      base = v;
      if (cost != null && cost > 0) pct = (base / cost - 1) * 100;
    }
    onFormation(g.product_id, {
      cost_price: cost ?? 0,
      markup_pct: pct != null ? Math.round(pct * 100) / 100 : 0,
      base_price: base,
    });
  }
  /** Base-price blur: routed through formation when a purchase price exists,
   *  so the coefficient follows a hand-set base. */
  function baseBlur(g: Group, raw: string) {
    if (onFormation && g.cost_price != null && g.cost_price > 0) {
      saveFormation(g, "base", raw);
    } else {
      onSaveBase(g.product_id, raw);
    }
  }
</script>

{#if !loading}
  <div class="dp-filters">
  <CatalogFilterBar
    bind:q={searchQ}
    bind:category={selectedCategory}
    bind:subcategory={selectedSubcategory}
    bind:supplier={selectedSupplier}
    {categories}
    suppliers={supplierOptions}
    oncolumns={() => (columnsOpen = true)}
  >
    {#snippet options()}
    {#if targets.length > 0}
      <!-- "Все клиенты" travels inside the option list (not as allLabel) so
           the closed trigger reads "Выбрать клиента" — the field is an
           invitation to pick, not a claim that everyone fits on screen. -->
      <SearchSelect
        options={clientOptions}
        bind:value={selectedClient}
        placeholder={$_("prices.filter.selectClient")}
        searchPlaceholder={$_("prices.filter.clientSearch")}
      />
    {/if}
    {#if countryOptions.length > 0}
      <!-- Countries of confirmed clients only — this filters WHO, not what. -->
      <select class="row-select" bind:value={selectedCountry} aria-label={$_("prices.filter.allCountries")}>
        <option value="">{$_("prices.filter.allCountries")}</option>
        {#each countryOptions as c (c)}
          <option value={c}>{c}</option>
        {/each}
      </select>
    {/if}
    {#if accessOn && onToggleExclusive && targets.length > 0}
      <span class="excl-mode">
        <button
          type="button"
          class="excl-btn"
          class:on={exclusiveMode}
          aria-pressed={exclusiveMode}
          title={$_("access.exclusive.mode")}
          onclick={() => (exclusiveMode = !exclusiveMode)}
        >
          <span class="material-icons-outlined">{exclusiveMode ? "star" : "star_border"}</span>
          {$_("access.exclusive.mode")}
        </button>
        <InfoTooltip text={$_("access.exclusive.help")} />
      </span>
    {/if}
    {/snippet}
  </CatalogFilterBar>
  </div>
{/if}

{#if loading}
  <div class="loading">{$_("common.loading")}</div>
{:else if grouped.length === 0}
  <div class="empty-state">
    <span class="material-icons-outlined empty-icon">price_change</span>
    <p>{targets.length === 0 ? emptyText : $_("prices.empty")}</p>
  </div>
{:else}
  <!-- With no partner linked yet the matrix used to show nothing at all, which
       read as "my products never got here". The products are listed with their
       base price; the target columns appear when there is a target. -->
  {#if targets.length === 0}
    <div class="no-targets">
      <span class="material-icons-outlined" aria-hidden="true">info</span>
      <span>{emptyText}</span>
    </div>
  {:else if infoNote}
    <!-- What makes a row visible to a client: access open AND a price set.
         Without this line, a priced-but-closed (or open-but-unpriced) product
         "not arriving" at the client reads as a bug. -->
    <div class="no-targets">
      <span class="material-icons-outlined" aria-hidden="true">info</span>
      <span>{infoNote}</span>
      {#if onHideNote}
        <button type="button" class="note-hide" onclick={onHideNote}>{$_("common.hide")}</button>
      {/if}
    </div>
  {/if}
  <div class="matrix-wrap">
    <table class="matrix-table col-divided">
      <thead>
        <tr class="head-row">
          {#each shownCols as col (col.key)}
            {#if col.key === "image"}
              <th class="img-col" style={colWidths.style(col.key)}>{col.label}<span
                  class="col-grip"
                  aria-hidden="true"
                  use:colWidths.grip={col.key}
                ></span></th>
            {:else}
              <th
                class={col.key === "product" ? "prod-col" : ["basePrice", "costPrice", "markup"].includes(col.key) ? "base-col" : "ref-col"}
                style={colWidths.style(col.key)}
                aria-sort={sortKey === col.key ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
              >
                <span class="base-all-head">
                  <button type="button" class="sort-btn" class:active={sortKey === col.key} onclick={() => sortBy(col.key)}>
                    {col.label}
                    <span class="material-icons-outlined sort-icon" aria-hidden="true">{sortIcon(col.key)}</span>
                  </button>
                  {#if col.key === "markup"}<InfoTooltip text={$_("prices.formation.tooltip")} />{/if}
                </span>
                <span
                  class="col-grip"
                  aria-hidden="true"
                  use:colWidths.grip={col.key}
                ></span>
              </th>
            {/if}
          {/each}
          {#each visibleTargets as t}
            <th class="target-col" style={colWidths.style(`t${t.id}`)}>
              <div class="target-name">{t.name}</div>
              {#if t.country}
                <div class="target-country">{t.country.toUpperCase()}</div>
              {/if}
              {#if onBaseToTarget || accessOn}
                <!-- Left: access (or, in star mode, exclusivity) for the whole
                     filtered view — over the access controls in the rows.
                     Right: the base price → this client. -->
                <div class="target-bulk">
                  {#if accessOn}
                    {#if exclusiveMode}
                      <button
                        type="button"
                        class="star-btn"
                        class:on={targetAllExclusive(t.id)}
                        aria-label={$_("prices.exclusive.bulkTooltip")}
                        onmouseenter={(e) => showTip(e, $_("prices.exclusive.bulkTooltip"))}
                        onmouseleave={hideTip}
                        disabled={!accessEditable}
                        onclick={() => toggleTargetAccess(t.id)}
                      >
                        <span class="material-icons-outlined">{targetAllExclusive(t.id) ? "star" : "star_border"}</span>
                      </button>
                    {:else}
                      <span
                        class="acc-tip acc-tip--access"
                        onmouseenter={(e) => showTip(e, $_("prices.bulkAccess.tooltip"))}
                        onmouseleave={hideTip}
                        role="presentation"
                      >
                        <input
                          type="checkbox"
                          class="access-box"
                          checked={targetAllAccessible(t.id)}
                          disabled={!accessEditable}
                          aria-label={$_("prices.bulkAccess.tooltip")}
                          onclick={(e) => {
                            e.preventDefault();
                            toggleTargetAccess(t.id);
                          }}
                        />
                      </span>
                    {/if}
                  {/if}
                  {#if onBaseToTarget}
                  <span
                    class="acc-tip"
                    onmouseenter={(e) => showTip(e, $_("prices.bulkBase.tooltip"))}
                    onmouseleave={hideTip}
                    role="presentation"
                  >
                    <input
                      type="checkbox"
                      checked={targetAllAtBase(t.id)}
                      aria-label={$_("prices.bulkBase.tooltip")}
                      onclick={(e) => {
                        e.preventDefault();
                        applyBaseToTarget(t);
                      }}
                    />
                  </span>
                  {/if}
                </div>
              {/if}
              <span
                class="col-grip"
                aria-hidden="true"
                use:colWidths.grip={`t${t.id}`}
              ></span>
            </th>
          {/each}
          <!-- Absorbs the leftover width so the data columns stay packed to
               the left instead of spreading across the whole screen. -->
          <th class="fill-col" aria-hidden="true"></th>
        </tr>
      </thead>
      <tbody>
        {#each grouped as g (g.product_id)}
          <tr
            class:not-orderable={!g.is_orderable}
            title={g.is_orderable ? undefined : $_("prices.row.notOrderable")}
          >
            {#each shownCols as col (col.key)}
              {#if col.key === "basePrice"}
                <td class="base-cell">
                  <input
                    type="number"
                    class="price-input"
                    value={money(g.base_price)}
                    placeholder="-"
                    step="0.01"
                    min="0"
                    onblur={(e) => baseBlur(g, (e.target as HTMLInputElement).value)}
                    onkeydown={focusNextInput}
                  />
                </td>
              {:else if col.key === "costPrice"}
                <td class="base-cell">
                  <input
                    type="number"
                    class="price-input"
                    value={money(g.cost_price)}
                    placeholder="-"
                    step="0.01"
                    min="0"
                    onblur={(e) => saveFormation(g, "cost", (e.target as HTMLInputElement).value)}
                    onkeydown={focusNextInput}
                  />
                </td>
              {:else if col.key === "markup"}
                <td class="base-cell">
                  <input
                    type="number"
                    class="price-input coef-input"
                    value={coefText(g)}
                    placeholder="-"
                    step="0.01"
                    min="0"
                    onblur={(e) => saveFormation(g, "coef", (e.target as HTMLInputElement).value)}
                    onkeydown={focusNextInput}
                  />
                </td>
              {:else if col.key === "product"}
                <td class="prod-cell">{cellText(g, col.key)}<SupplierGapBadge reason={g.supplier_gap_reason} /></td>
              {:else if col.key === "image"}
                <td class="img-cell">
                  {#if g.image_url}
                    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                    <img
                      src={g.thumb_url ?? g.image_url}
                      alt=""
                      loading="lazy"
                      onmouseenter={(e) => showImgPreview(e, g.image_url!)}
                      onmouseleave={hideImgPreview}
                    />
                  {:else}<div class="img-placeholder">📦</div>{/if}
                </td>
              {:else}
                <td class="ref-cell">{cellText(g, col.key)}</td>
              {/if}
            {/each}
            {#each visibleTargets as t}
              {@const cell = g.cells.get(t.id)}
              {@const closed = !!cell && !cell.is_accessible}
              <td class="price-cell" class:overridden={isOverridden(g, cell?.price ?? null)} class:inaccessible={closed}>
                <div class="price-line">
                  {#if accessOn}
                    <!-- Access for this client, left of its price. -->
                    {#if cell?.is_exclusive}
                      <button
                        type="button"
                        class="star-btn on"
                        aria-label={$_("prices.exclusive.tooltip")}
                        onmouseenter={(e) => showTip(e, $_("prices.exclusive.tooltip"))}
                        onmouseleave={hideTip}
                        disabled={!accessEditable}
                        onclick={() => toggleCellAccess(g, t.id)}
                      >
                        <span class="material-icons-outlined">star</span>
                      </button>
                    {:else}
                      <span
                        class="acc-tip"
                        onmouseenter={(e) =>
                          showTip(e, exclusiveMode ? $_("prices.exclusive.tooltip") : $_("prices.accessible.tooltip"))}
                        onmouseleave={hideTip}
                        role="presentation"
                      >
                        <input
                          type="checkbox"
                          class="access-box"
                          checked={cell?.is_accessible ?? false}
                          disabled={!accessEditable}
                          aria-label={$_("prices.accessible.tooltip")}
                          onclick={(e) => {
                            e.preventDefault();
                            toggleCellAccess(g, t.id);
                          }}
                        />
                      </span>
                    {/if}
                  {/if}
                  <!-- A dimmed cell stays editable on purpose: access is the
                       master switch, and the two halves of "the client sees
                       this" can be done in either order. Pricing a closed row
                       ahead of time is normal — ticking the box beside it
                       then publishes an already-priced position. The
                       hover says so, because a greyed-out-looking field you
                       can still type in otherwise reads as a bug. -->
                  <input
                    type="number"
                    class="price-input"
                    value={money(cell?.price)}
                    placeholder="-"
                    step="0.01"
                    min="0"
                    title={closed ? $_("prices.cell.accessClosed") : undefined}
                    onblur={(e) => onSavePrice(g.product_id, t.id, (e.target as HTMLInputElement).value)}
                    onkeydown={focusNextInput}
                  />
                </div>
              </td>
            {/each}
            <td class="fill-cell"></td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<ColumnSettings
  bind:open={columnsOpen}
  columns={COLUMNS}
  bind:visible={colVisible}
  bind:order={colOrder}
/>

{#if tip}
  <div class="hover-tip" role="tooltip" style="left: {tip.x}px; top: {tip.y}px;">{tip.text}</div>
{/if}

<!-- Thumbnail hover preview. Fixed-positioned and rendered outside the table
     so the matrix's overflow scroll can't clip it. -->
{#if imgPreview}
  <div class="img-preview" style="left: {imgPreview.x}px; top: {imgPreview.y}px;">
    <img src={imgPreview.url} alt="" />
  </div>
{/if}

<style>
  .dp-filters { margin-bottom: 12px; }
  .row-select { padding: 7px 10px; border: 1px solid var(--color-divider); border-radius: 6px; font-size: 13px; font-family: inherit; background: #fff; }
  .loading { padding: 48px; text-align: center; color: var(--color-text-muted); }
  .empty-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px; color: var(--color-text-muted); }
  .empty-icon { font-size: 48px !important; opacity: 0.3; }
  .matrix-wrap { background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; overflow: auto; max-height: 70vh; }
  .matrix-table { border-collapse: separate; border-spacing: 0; font-size: 13px; min-width: 100%; }
  /* Tight horizontal padding + the trailing filler column keep the data
     columns packed to the left instead of spread across the full width —
     the eye shouldn't have to travel the whole screen per row. */
  /* Склад's header row (owner, 2026-09-19). */
  .matrix-table th { padding: 9px 10px; border-bottom: 1px solid var(--cat-th-line); background: var(--cat-th-bg); font-size: 10px; font-weight: 700; color: var(--cat-th-color); text-transform: uppercase; letter-spacing: 0.04em; position: sticky; top: 0; z-index: 3; }
  .sort-btn {
    display: inline-flex; align-items: center; gap: 2px; padding: 0; border: none; background: none;
    font: inherit; text-transform: inherit; letter-spacing: inherit; color: inherit; cursor: pointer;
  }
  .sort-btn:hover, .sort-btn.active { color: var(--color-primary); }
  .sort-icon { font-size: 13px !important; opacity: 0.45; }
  .sort-btn.active .sort-icon { opacity: 1; }
  .no-targets {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 12px; padding: 10px 14px; border-radius: 8px;
    background: rgba(33,133,213,0.06); border: 1px solid rgba(33,133,213,0.22);
    font-size: 13px; color: var(--color-text);
  }
  .no-targets .material-icons-outlined { font-size: 18px; color: var(--color-primary); }
  .note-hide {
    margin-left: auto; flex-shrink: 0; border: none; background: none; cursor: pointer;
    font-family: inherit; font-size: 13px; font-weight: 600; color: var(--color-primary); padding: 2px 4px;
  }
  .note-hide:hover { text-decoration: underline; }
  .matrix-table td { padding: var(--cat-cell-y) var(--cat-cell-x); border-bottom: 1px solid var(--color-divider); background: #fff; vertical-align: top; }
  .matrix-table tr:last-child td { border-bottom: none; }
  .prod-col { text-align: left; min-width: 170px; }
  /* Barcode / qty-per-pack / weight read left-aligned in their cells, so their
     headings do too — centred captions over a left-aligned column read as a
     column that has slipped. */
  .ref-col { text-align: left; }
  .target-col { text-align: center; min-width: 90px; }
  .fill-col, .fill-cell { width: 100%; padding: 0; }
  .prod-cell { font-weight: 500; }
  .ref-cell { color: var(--color-text-muted); font-size: 12px; white-space: nowrap; }
  .img-col { text-align: left; }
  .img-cell { width: 48px; }
  .img-cell img { width: 40px; height: 40px; object-fit: contain; border-radius: 4px; background: #fff; border: 1px solid #eee; cursor: zoom-in; display: block; }
  .img-cell img:hover { border-color: var(--color-primary); }
  .img-placeholder { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: #f0f0f0; border-radius: 4px; opacity: 0.5; font-size: 20px; }
  .img-preview {
    position: fixed; z-index: 1200; pointer-events: none;
    width: 320px; height: 320px;
    background: #fff; border: 1px solid var(--color-divider); border-radius: 8px;
    box-shadow: 0 12px 36px rgba(0,0,0,0.22);
    padding: 8px; box-sizing: border-box;
    display: flex; align-items: center; justify-content: center;
  }
  .img-preview img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .target-name { margin-bottom: 2px; }
  .target-country { font-size: 10px; color: var(--color-text-muted); letter-spacing: 0.08em; margin-bottom: 4px; }
  /* Access left, base-price right — over the same spots in the rows below. */
  .target-bulk { display: flex; align-items: center; justify-content: center; gap: 6px; }
  .target-bulk input { margin: 0; cursor: pointer; }
  .base-all-head { display: inline-flex; align-items: center; gap: 4px; }
  .price-cell { text-align: center; }
  .price-cell.overridden { background: #fff7d6; }
  .price-cell.inaccessible .price-input { opacity: 0.4; }
  /* The access control is small and sits tight against its price. */
  .access-box { width: 13px; height: 13px; margin: 0; cursor: pointer; accent-color: var(--color-primary); }
  .access-box:disabled { cursor: default; }
  .star-btn {
    display: inline-flex; align-items: center; justify-content: center;
    border: none; background: none; padding: 0; cursor: pointer; color: #c3cad1; line-height: 1;
  }
  .star-btn .material-icons-outlined { font-size: 17px !important; }
  .star-btn:hover { color: #e9a212; }
  .star-btn.on { color: #f0a70a; }
  .excl-mode { display: inline-flex; align-items: center; gap: 4px; }
  .excl-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 7px 12px; border: 1px solid var(--color-divider); border-radius: 6px;
    background: #fff; font-size: 13px; font-family: inherit; cursor: pointer; color: var(--color-text);
  }
  .excl-btn .material-icons-outlined { font-size: 17px !important; color: #c3cad1; }
  .excl-btn.on { border-color: #f0a70a; color: #8a6100; background: #fff8e6; }
  .excl-btn.on .material-icons-outlined { color: #f0a70a; }
  /* «Не доступна к заказу» on the card: grey down the whole row. */
  tr.not-orderable td { background: #eceff1; color: var(--color-text-muted); }
  tr.not-orderable td.price-cell.overridden { background: #e6e2cf; }
  .price-line { display: flex; align-items: center; justify-content: center; gap: 6px; }
  .acc-tip { display: inline-flex; flex-shrink: 0; }

  .hover-tip {
    position: fixed; z-index: 1100; pointer-events: none;
    transform: translate(-50%, calc(-100% - 8px));
    width: max-content; max-width: 260px;
    background: var(--color-surface-darker, #303841); color: #fff;
    padding: 8px 11px; border-radius: 8px; font-size: 12.5px; font-weight: 400;
    line-height: 1.45; box-shadow: 0 6px 20px rgba(0,0,0,0.25);
    white-space: normal; text-align: left;
  }
  .hover-tip::after {
    content: ""; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
    border: 6px solid transparent; border-top-color: var(--color-surface-darker, #303841);
  }
  .price-input { width: 76px; padding: 5px 8px; border: 1px solid var(--color-divider); border-radius: 4px; font-size: 13px; text-align: right; font-family: inherit; appearance: textfield; -moz-appearance: textfield; }
  /* Prices are typed, not clicked — the number-input spinners only invite
     accidental ±0.01 nudges (2026-08-14 feedback). */
  .price-input::-webkit-outer-spin-button, .price-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .coef-input { width: 58px; }
  .price-input:focus { outline: none; border-color: var(--color-primary); }
</style>
